<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useSession } from '../auth';
import { trpc } from '../trpc';
import AppHeader from '@/components/layout/AppHeader.vue';
import { Button } from '@/components/ui/button';

interface Flashcard {
  id: string;
  shortId: string;
  userId: string;
  deckId: string;
  question: string;
  answer: string;
  due: string;
  stability: number;
  difficulty: number;
  scheduledDays: number;
  reps: number;
  lapses: number;
  learningSteps: number;
  state: number;
  lastReview: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ReviewStats {
  byState: Record<number, number>;
  dueNow: number;
  reviewsToday: number;
}

interface Deck {
  id: string;
  name: string;
}

interface DueCount {
  deckId: string;
  count: number;
}

const router = useRouter();
const route = useRoute();
const session = useSession();
const user = computed(() => session.value?.data?.user);
const isLoading = computed(() => session.value?.isPending);

// Get optional deckId from query params
const deckId = computed(() => route.query.deckId as string | undefined);

const dueCards = ref<Flashcard[]>([]);
const decks = ref<Deck[]>([]);
const currentIndex = ref(0);
const showAnswer = ref(false);
const loading = ref(false);
const submitting = ref(false);
const error = ref('');
const stats = ref<ReviewStats | null>(null);
const sessionReviewCount = ref(0);
const pressedRating = ref<number | null>(null);

// Edit modal state
const showEditModal = ref(false);
const editQuestion = ref('');
const editAnswer = ref('');
const saving = ref(false);

// Undo state
const lastReviewedCard = ref<Flashcard | null>(null);

// Deck selection state
const reviewStarted = ref(false);
const dueCountsByDeck = ref<DueCount[]>([]);

const deckNameMap = computed(() => {
  const map: Record<string, string> = {};
  for (const deck of decks.value) {
    map[deck.id] = deck.name;
  }
  return map;
});

const decksWithDueCounts = computed(() => {
  const countMap: Record<string, number> = {};
  for (const { deckId, count } of dueCountsByDeck.value) {
    countMap[deckId] = count;
  }
  return decks.value
    .map((deck) => ({
      ...deck,
      dueCount: countMap[deck.id] || 0,
    }))
    .filter((deck) => deck.dueCount > 0);
});

const totalDueCount = computed(() => {
  return dueCountsByDeck.value.reduce((sum, { count }) => sum + count, 0);
});

const currentCard = computed(() => dueCards.value[currentIndex.value] ?? null);
const isComplete = computed(() => currentIndex.value >= dueCards.value.length && dueCards.value.length > 0);

const ratingButtons = [
  { rating: 1, label: 'Again', description: "Didn't remember", color: 'rating-again' },
  { rating: 2, label: 'Hard', description: 'Took effort', color: 'rating-hard' },
  { rating: 3, label: 'Good', description: 'Recalled well', color: 'rating-good' },
  { rating: 4, label: 'Easy', description: 'Instant recall', color: 'rating-easy' },
];

async function fetchDueCards(overrideDeckId?: string) {
  loading.value = true;
  error.value = '';
  try {
    const params: { limit: number; deckId?: string } = { limit: 50 };
    const effectiveDeckId = overrideDeckId ?? deckId.value;
    if (effectiveDeckId) {
      params.deckId = effectiveDeckId;
    }
    dueCards.value = await trpc.getDueFlashcards.query(params);
    currentIndex.value = 0;
    showAnswer.value = false;
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to fetch cards';
  } finally {
    loading.value = false;
  }
}

async function fetchStats() {
  try {
    stats.value = await trpc.getReviewStats.query();
  } catch (e) {
    console.error('Failed to fetch stats:', e);
  }
}

async function fetchDecks() {
  try {
    decks.value = await trpc.listDecks.query();
  } catch (e) {
    console.error('Failed to fetch decks:', e);
  }
}

async function fetchDueCountsByDeck() {
  try {
    dueCountsByDeck.value = await trpc.getDueCountsByDeck.query();
  } catch (e) {
    console.error('Failed to fetch due counts:', e);
  }
}

function startReview(selectedDeckId?: string) {
  if (selectedDeckId) {
    router.replace({ query: { deckId: selectedDeckId } });
  }
  reviewStarted.value = true;
  fetchDueCards(selectedDeckId);
  fetchStats();
}

function revealAnswer() {
  showAnswer.value = true;
}

async function submitRating(rating: number) {
  if (!currentCard.value || submitting.value) return;

  submitting.value = true;
  pressedRating.value = rating;
  error.value = '';

  try {
    await trpc.submitReview.mutate({
      flashcardId: currentCard.value.id,
      rating,
    });

    // Save for undo before advancing
    lastReviewedCard.value = { ...currentCard.value };

    // Brief delay for visual feedback
    await new Promise(resolve => setTimeout(resolve, 200));

    sessionReviewCount.value++;
    pressedRating.value = null;
    showAnswer.value = false;
    currentIndex.value++;

    // If we've gone through all cards, fetch more or show completion
    if (currentIndex.value >= dueCards.value.length) {
      await fetchDueCards();
      await fetchStats();
    }
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to submit review';
  } finally {
    submitting.value = false;
  }
}

function goBack() {
  if (!lastReviewedCard.value) return;

  // Insert the last reviewed card back at the current position
  dueCards.value.splice(currentIndex.value, 0, lastReviewedCard.value);
  lastReviewedCard.value = null;
  showAnswer.value = false;
}

function handleKeydown(event: KeyboardEvent) {
  // Handle Escape for closing edit modal
  if (event.key === 'Escape' && showEditModal.value) {
    closeEditModal();
    return;
  }

  // Don't process review shortcuts when modal is open
  if (showEditModal.value || submitting.value) return;

  // Backspace to go back to last reviewed card
  if (event.key === 'Backspace' && lastReviewedCard.value) {
    event.preventDefault();
    goBack();
    return;
  }

  if (!showAnswer.value) {
    // Space or Enter to reveal
    if (event.code === 'Space' || event.code === 'Enter') {
      event.preventDefault();
      revealAnswer();
    }
  } else {
    // 1-4 to rate
    const rating = parseInt(event.key);
    if (rating >= 1 && rating <= 4) {
      event.preventDefault();
      submitRating(rating);
    }
  }
}

function startNewSession() {
  sessionReviewCount.value = 0;
  fetchDueCards();
  fetchStats();
}

function openEditModal() {
  if (!currentCard.value) return;
  editQuestion.value = currentCard.value.question;
  editAnswer.value = currentCard.value.answer;
  showEditModal.value = true;
}

function closeEditModal() {
  showEditModal.value = false;
  editQuestion.value = '';
  editAnswer.value = '';
}

async function saveEdit() {
  if (!currentCard.value || !editQuestion.value.trim() || !editAnswer.value.trim()) return;
  saving.value = true;
  try {
    await trpc.updateFlashcard.mutate({
      idOrShortId: currentCard.value.id,
      question: editQuestion.value.trim(),
      answer: editAnswer.value.trim(),
    });
    // Update the card in the local array
    currentCard.value.question = editQuestion.value.trim();
    currentCard.value.answer = editAnswer.value.trim();
    closeEditModal();
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to update flashcard';
  } finally {
    saving.value = false;
  }
}

function initializeReview() {
  fetchDecks();
  window.addEventListener('keydown', handleKeydown);

  // If deckId is in URL, go straight to review
  if (deckId.value) {
    reviewStarted.value = true;
    fetchDueCards();
    fetchStats();
  } else {
    // Show deck selection
    fetchDueCountsByDeck();
  }
}

onMounted(() => {
  if (!isLoading.value && !user.value) {
    router.push('/sign-in');
  } else if (user.value) {
    initializeReview();
  }
});

watch(user, (newUser, oldUser) => {
  if (newUser) {
    // Add listener if it wasn't added on mount
    if (!oldUser) {
      initializeReview();
    }
  }
});

// Cleanup keyboard listener
import { onUnmounted } from 'vue';
onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
  <div class="min-h-screen bg-background">
    <AppHeader />

    <main class="max-w-2xl mx-auto px-4 py-8">
      <div
        v-if="isLoading"
        class="text-center text-muted-foreground"
      >
        Loading...
      </div>

      <div
        v-else-if="user"
        class="space-y-6"
      >
        <!-- Header -->
        <div>
          <h1 class="text-2xl font-semibold">Review</h1>
        </div>

        <!-- Error message -->
        <div
          v-if="error"
          class="bg-destructive/10 text-destructive p-4 rounded-md"
        >
          {{ error }}
        </div>

        <!-- Deck selection (before review starts) -->
        <div
          v-if="!reviewStarted"
          class="space-y-4"
        >
          <!-- Review All button -->
          <Button
            v-if="totalDueCount > 0"
            class="w-full"
            size="lg"
            @click="startReview()"
          >
            Review All ({{ totalDueCount }} cards)
          </Button>

          <!-- Deck list -->
          <div
            v-if="decksWithDueCounts.length > 0"
            class="space-y-2"
          >
            <div class="text-sm text-muted-foreground">
              Or choose a deck:
            </div>
            <div
              v-for="deck in decksWithDueCounts"
              :key="deck.id"
              class="flex justify-between items-center p-4 bg-card rounded-lg border border-border hover:border-primary cursor-pointer transition-colors"
              @click="startReview(deck.id)"
            >
              <span class="font-medium">{{ deck.name }}</span>
              <span class="text-sm text-muted-foreground">{{ deck.dueCount }} due</span>
            </div>
          </div>

          <!-- No cards due at all -->
          <div
            v-if="totalDueCount === 0 && decks.length > 0"
            class="text-center py-12 bg-card rounded-lg border border-border"
          >
            <div class="text-4xl mb-4">
              &#10003;
            </div>
            <h2 class="text-xl font-semibold mb-2">All caught up!</h2>
            <p class="text-muted-foreground mb-4">
              No cards are due for review right now.
            </p>
            <Button
              variant="outline"
              @click="router.push('/decks')"
            >
              Back to Decks
            </Button>
          </div>
        </div>

        <!-- Review mode -->
        <template v-else>
          <!-- Loading state -->
          <div
            v-if="loading"
            class="text-center py-12"
          >
            <div class="text-muted-foreground">Loading cards...</div>
          </div>

          <!-- No cards due -->
          <div
            v-else-if="dueCards.length === 0 && !isComplete"
            class="text-center py-12 bg-card rounded-lg border border-border"
          >
          <div class="text-4xl mb-4">
            &#10003;
          </div>
          <h2 class="text-xl font-semibold mb-2">All caught up!</h2>
          <p class="text-muted-foreground mb-4">
            No cards are due for review right now.
          </p>
          <div class="space-x-2">
            <Button
              variant="outline"
              @click="router.push('/decks')"
            >
              Add More Cards
            </Button>
            <Button @click="startNewSession">
              Check Again
            </Button>
          </div>
        </div>

        <!-- Session complete -->
        <div
          v-else-if="isComplete"
          class="text-center py-12 bg-card rounded-lg border border-border"
        >
          <div class="text-4xl mb-4">
            &#127881;
          </div>
          <h2 class="text-xl font-semibold mb-2">Session Complete!</h2>
          <p class="text-muted-foreground mb-4">
            You reviewed {{ sessionReviewCount }} cards this session.
          </p>
          <div class="space-x-2">
            <Button
              variant="outline"
              @click="router.push('/decks')"
            >
              Back to Decks
            </Button>
            <Button @click="startNewSession">
              Continue Reviewing
            </Button>
          </div>
        </div>

        <!-- Review card -->
        <div
          v-else-if="currentCard"
          class="space-y-4"
        >
          <!-- Progress indicator -->
          <div class="flex justify-between items-center text-sm text-muted-foreground">
            <span>Card {{ currentIndex + 1 }} of {{ dueCards.length }}</span>
            <button
              v-if="lastReviewedCard"
              class="flex items-center gap-1 px-2 py-1 bg-muted hover:bg-muted/80 rounded text-xs cursor-pointer transition-colors"
              @click="goBack"
            >
              <span>&larr;</span>
              <span>Back</span>
              <kbd class="ml-1 px-1 py-0.5 bg-background rounded text-[10px]">Bksp</kbd>
            </button>
          </div>

          <!-- Flashcard -->
          <div class="bg-card rounded-lg border border-border shadow-lg overflow-hidden">
            <!-- Question -->
            <div class="p-6 border-b border-border">
              <div class="flex justify-between items-center mb-2">
                <div class="flex items-center gap-2">
                  <span class="text-xs text-muted-foreground uppercase tracking-wide">
                    Question
                  </span>
                  <span
                    v-if="deckNameMap[currentCard.deckId]"
                    class="px-2 py-1 bg-primary/10 text-primary rounded text-xs"
                  >
                    {{ deckNameMap[currentCard.deckId] }}
                  </span>
                </div>
                <code class="text-xs text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded">
                  {{ currentCard.shortId }}
                </code>
              </div>
              <div class="text-lg whitespace-pre-wrap">
                {{ currentCard.question }}
              </div>
            </div>

            <!-- Answer (revealed) -->
            <div
              v-if="showAnswer"
              class="p-6 bg-muted/30"
            >
              <div class="text-xs text-muted-foreground uppercase tracking-wide mb-2">
                Answer
              </div>
              <div class="text-lg whitespace-pre-wrap">
                {{ currentCard.answer }}
              </div>
              <div class="mt-3 text-right">
                <button
                  class="text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                  @click="openEditModal"
                >
                  Edit card
                </button>
              </div>
            </div>

            <!-- Reveal button -->
            <div
              v-else
              class="p-6"
            >
              <Button
                class="w-full"
                size="lg"
                @click="revealAnswer"
              >
                Show Answer
                <span class="ml-2 text-xs opacity-70">(Space)</span>
              </Button>
            </div>
          </div>

          <!-- Rating buttons -->
          <div
            v-if="showAnswer"
            class="space-y-3"
          >
            <div class="text-center text-sm text-muted-foreground">
              How well did you remember?
            </div>
            <div class="grid grid-cols-4 gap-2">
              <button
                v-for="btn in ratingButtons"
                :key="btn.rating"
                :disabled="submitting"
                class="flex flex-col items-center p-3 rounded-lg text-white transition-all disabled:opacity-50"
                :class="[
                  btn.color,
                  pressedRating === btn.rating ? 'ring-4 ring-white ring-offset-2 ring-offset-background scale-105' : ''
                ]"
                @click="submitRating(btn.rating)"
              >
                <span class="font-semibold">{{ btn.label }}</span>
                <span class="text-xs opacity-80">{{ btn.description }}</span>
              </button>
            </div>
          </div>

          <!-- Keyboard hint -->
          <div
            v-if="showAnswer"
            class="text-center text-xs text-muted-foreground"
          >
            Press <kbd class="px-1 py-0.5 bg-muted rounded">1</kbd>-<kbd class="px-1 py-0.5 bg-muted rounded">4</kbd> to rate
          </div>
        </div>
        </template>

      </div>

      <div
        v-else
        class="text-center"
      >
        <p class="text-muted-foreground mb-4">
          You need to sign in to review cards.
        </p>
        <Button @click="router.push('/sign-in')">
          Sign In
        </Button>
      </div>
    </main>

    <!-- Edit Modal -->
    <div
      v-if="showEditModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
    >
      <div class="bg-card rounded-lg shadow-xl p-6 w-full max-w-lg border border-border">
        <h3 class="text-lg font-semibold mb-4">
          Edit Flashcard
        </h3>
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-foreground mb-1">Question</label>
            <textarea
              v-model="editQuestion"
              placeholder="Enter your question"
              rows="3"
              class="w-full px-3 py-2 border border-input rounded-md bg-background"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-foreground mb-1">Answer</label>
            <textarea
              v-model="editAnswer"
              placeholder="Enter the answer"
              rows="3"
              class="w-full px-3 py-2 border border-input rounded-md bg-background"
            />
          </div>
        </div>
        <div class="flex justify-end gap-2 mt-4">
          <Button
            variant="ghost"
            @click="closeEditModal"
          >
            Cancel
          </Button>
          <Button
            :disabled="saving || !editQuestion.trim() || !editAnswer.trim()"
            @click="saveEdit"
          >
            {{ saving ? 'Saving...' : 'Save' }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
